import React, { ReactElement, useEffect, useState } from 'react';
import {
  SearchActionFilter,
  SearchActionFilterType,
} from '@enview/interface/types/Bill';
import { OptionType } from '../../Shared/DropDowns/MultipleSelectDropDown';
import CustomDropdown from '../../Shared/DropDowns/CustomDropdown';
import DropDownButton from '../../Shared/Buttons/DropDownButton';
import SingleSelectForm from '../../Shared/Forms/SingleSelectForm';
import DateRangeInputDropdown from '../../Shared/Selectors/DateRangeInputDropdown';

type BillActionFilterProps = {
  billAction: SearchActionFilter | undefined;
  setBillAction?: (arg: SearchActionFilter | undefined) => void;
  showActionFilter: boolean;
  setShowActionFilter: (bool: boolean) => void;
  setSearchChanged: (bool: boolean) => void;
};

const actionOptions: OptionType<SearchActionFilterType>[] = [
  { label: 'First Action', value: SearchActionFilterType.FIRST_ACTION },
  { label: 'Latest Action', value: SearchActionFilterType.LATEST_ACTION },
  { label: 'Any Action', value: SearchActionFilterType.ANY_ACTION },
  { label: 'Introduction', value: SearchActionFilterType.INTRODUCTION },
  { label: 'Passage', value: SearchActionFilterType.PASSAGE },
  { label: 'Amendment', value: SearchActionFilterType.AMENDMENT },
];

const BillActionFilter = (props: BillActionFilterProps): ReactElement => {
  const {
    billAction,
    setBillAction,
    showActionFilter,
    setShowActionFilter,
    setSearchChanged,
  } = props;

  const [actionType, setActionType] = useState(
    billAction ? billAction.type : undefined,
  );
  const [fromDate, setFromDate] = useState(
    billAction ? billAction.fromDate : undefined,
  );
  const [toDate, setToDate] = useState(billAction ? billAction.toDate : undefined);

  // Dropdown display states
  const [showTypeFilter, setShowTypeFilter] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();
  // Kept separate from save validation errors so that Save stays blocked while a typed date is
  // rejected, rather than being cleared by a successful save check.
  const [entryError, setEntryError] = useState<string>();
  // Changing the key remounts the date inputs, which discards rejected text that clearing an
  // already empty date would otherwise leave on screen.
  const [dateInputsKey, setDateInputsKey] = useState(0);

  const resetDateEntry = (): void => {
    setEntryError(undefined);
    setDateInputsKey((key) => key + 1);
  };

  useEffect(() => {
    if (
      !billAction &&
      (actionType || fromDate || toDate || errorMessage || entryError)
    ) {
      setActionType(undefined);
      setFromDate(undefined);
      setToDate(undefined);
      setErrorMessage(undefined);
      resetDateEntry();
    }

    // re-set all billAction filters when props change, namely after pressing the browser back button
    if (billAction) {
      if (billAction.type !== actionType) {
        setActionType(billAction.type);
      }
      if (billAction.fromDate !== fromDate) {
        setFromDate(billAction.fromDate);
      }
      if (billAction.toDate !== toDate) {
        setToDate(billAction.toDate);
      }
    }
  }, [billAction]);

  useEffect(() => {
    if (!showActionFilter) setErrorMessage(undefined);
  }, [showActionFilter]);

  // Any date entry or calendar change replaces a previous save validation error.
  const handleEntryError = (message: string | undefined): void => {
    setEntryError(message);
    setErrorMessage(undefined);
  };

  const handleSave = (): void => {
    // The committed dates do not include a rejected typed date, so saving would apply a date other
    // than the one shown in its input.
    if (entryError) return;
    if (!actionType) {
      setErrorMessage('Please select a bill action.');
      return;
    }
    if (!fromDate && !toDate) {
      setErrorMessage('Please select a start date, an end date, or both.');
      return;
    }
    if (fromDate && toDate && fromDate > toDate) {
      setErrorMessage('Please select an end date later than the start date.');
      return;
    }
    const newAction = { type: actionType, fromDate, toDate };
    setErrorMessage(undefined);
    if (setBillAction) setBillAction(newAction);
    setShowActionFilter(false);
    setSearchChanged(true);
  };

  const handleClearButton = (): void => {
    if (billAction) {
      setSearchChanged(true);
    }
    if (setBillAction) setBillAction(undefined);
    setActionType(undefined);
    setFromDate(undefined);
    setToDate(undefined);
    setErrorMessage(undefined);
    resetDateEntry();
  };

  const dropdownTargetButton = (
    isDisplayed: boolean,
    label: string,
    onClick: () => void,
    icon?: ReactElement,
  ): ReactElement => (
    <DropDownButton
      dropdownDisplayed={isDisplayed}
      icon={icon}
      id={`${label.replace(/\s/g, '-').toLowerCase()}-filter`}
      label={label}
      onClick={onClick}
    />
  );

  const actionTypeLabel =
    actionOptions.find((option) => option.value === actionType)?.label || 'Bill Action';
  const displayedError = entryError ?? errorMessage;

  return (
    <>
      <div className="bill-action-filter">
        <h3>BILL ACTION DATE RANGE</h3>

        <CustomDropdown
          setShowDropdown={setShowTypeFilter}
          showDropdown={showTypeFilter}
          target={dropdownTargetButton(showTypeFilter, actionTypeLabel, () =>
            setShowTypeFilter(!showTypeFilter),
          )}
        >
          <div className="adv-search-selector">
            <SingleSelectForm
              currentSelection={actionType}
              options={actionOptions}
              singleColumn
              updateSelection={(type) => {
                setActionType(type);
                setShowTypeFilter(false);
              }}
            />
          </div>
        </CustomDropdown>
        <h3 style={{ marginTop: '24px' }}>DATES</h3>
        <DateRangeInputDropdown
          fromDate={fromDate}
          key={dateInputsKey}
          onEntryError={handleEntryError}
          setFromDate={setFromDate}
          setToDate={setToDate}
          toDate={toDate}
        />
        {displayedError ? <div className="error">{displayedError}</div> : <></>}
      </div>

      <div className="control-buttons">
        <button className="btn btn-action" onClick={() => handleSave()} type="submit">
          Save
        </button>
        <button
          className="btn btn-text"
          id="select-control-clear-all"
          onClick={handleClearButton}
          type="button"
        >
          <span>Clear All</span>
        </button>
      </div>
    </>
  );
};

export default BillActionFilter;
